import React, { useState, useEffect, createContext, useContext } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

// Types
interface User {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  email?: string | null;
  job_title?: string;
  role: string;
  organization_id?: number | null;
  home_id?: number | null;
  is_superadmin?: boolean;
  status?: string;
}

interface SessionInfo {
  isImpersonating: boolean;
  actor: User | null;
  user: User | null;
  startedAt: string | null;
  subscription?: {
    status: 'active' | 'suspended' | 'inactive';
    entity: 'organization' | 'home' | null;
  };
}

interface SessionContextType {
  session: SessionInfo | null;
  isLoading: boolean;
  ready: boolean;
  refreshSession: () => void;
  logout: () => Promise<void>;
  startImpersonation: (userId: number) => Promise<void>;
  stopImpersonation: () => Promise<void>;
}

const SessionContext = createContext<SessionContextType | null>(null);

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSession must be used within a SessionProvider');
  return context;
};

export const useWriteGuard = () => {
  const { session } = useSession();
  return {
    canWrite: !session?.isImpersonating,
    isReadOnly: session?.isImpersonating || false,
    showWriteBlockedMessage: () => ({
      title: 'Write operations disabled',
      description: 'You cannot modify data while impersonating another user.',
      variant: 'destructive' as const,
    }),
  };
};

export function SubscriptionBlockedModal({ subscriptionStatus, entity, onLogout }: { subscriptionStatus: 'suspended' | 'inactive'; entity: 'organization' | 'home' | null; onLogout: () => void; }) {
  const entityName = entity === 'organization' ? 'organization' : 'care home';
  const statusText = subscriptionStatus === 'suspended' ? 'Suspended' : 'Inactive';
  return (
    <Dialog open={true}>
      <DialogContent className="sm:max-w-md" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
              <AlertTriangle className="h-6 w-6 text-destructive" />
            </div>
            <div>
              <DialogTitle className="text-xl">Subscription {statusText}</DialogTitle>
              <DialogDescription className="mt-1">Access to the platform is currently unavailable</DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-4">
            <p className="text-sm text-muted-foreground">
              Your {entityName}'s subscription is currently <strong className="text-destructive">{statusText.toLowerCase()}</strong>.
              To regain access to the Smart Care Audits platform, please contact your administrator or reach out to our support team.
            </p>
          </div>
          <div className="space-y-2">
            <h4 className="text-sm font-medium">What you can do:</h4>
            <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
              <li>Contact your {entityName} administrator</li>
              <li>Email us at <a href="mailto:info@smartcareaudits.co.uk" className="text-primary hover:underline">info@smartcareaudits.co.uk</a></li>
              <li>Call us for immediate assistance</li>
            </ul>
          </div>
        </div>
        <DialogFooter className="flex-col sm:flex-col gap-2">
          <Button onClick={onLogout} variant="default" className="w-full" data-testid="button-logout-subscription-blocked">
            Log Out
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [ready, setReady] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const fetchSession = async () => {
    const abortController = new AbortController();
    const timeoutId = setTimeout(() => abortController.abort(), 5000);
    try {
      const response = await fetch('/api/session', { method: 'GET', credentials: 'include', signal: abortController.signal });
      clearTimeout(timeoutId);
      if (response.status === 401) {
        setSession(null);
        return;
      } else if (response.ok) {
        const data = await response.json();
        setSession({
          isImpersonating: data.isImpersonating,
          actor: data.actor,
          user: data.user,
          startedAt: data.started_at,
          subscription: data.subscription,
        });
      } else {
        setSession(null);
      }
    } catch (error: any) {
      setSession(null);
    } finally {
      setIsLoading(false);
      setReady(true);
    }
  };

  const refreshSession = () => {
    setIsLoading(true);
    setReady(false);
    fetchSession();
  };

  const logout = async () => {
    try {
      await apiRequest('POST', '/api/auth/logout');
      // clear client-side cache and redirect
      queryClient.clear();
      window.location.assign('/login');
    } catch (error) {
      queryClient.clear();
      window.location.assign('/login');
    }
  };

  const startImpersonation = async (userId: number) => {
    try {
      setIsLoading(true);
      const response = await apiRequest('POST', '/api/impersonate/start', { user_id: userId });
      await fetchSession();
      toast({ title: 'Impersonation started', description: 'You are now viewing as the selected user.' });
      window.location.href = '/dashboard';
    } catch (error: any) {
      toast({ title: 'Failed to start impersonation', description: error.message || 'Unable to impersonate user', variant: 'destructive' });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const stopImpersonation = async () => {
    try {
      setIsLoading(true);
      await apiRequest('POST', '/api/impersonate/stop');
      await fetchSession();
      toast({ title: 'Impersonation stopped', description: 'You have returned to your original account.' });
      window.location.href = '/dashboard';
    } catch (error: any) {
      toast({ title: 'Failed to stop impersonation', description: error.message || 'Unable to stop impersonation', variant: 'destructive' });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSession();
  }, []);

  return (
    <SessionContext.Provider value={{ session, isLoading, ready, refreshSession, logout, startImpersonation, stopImpersonation }}>
      {children}
    </SessionContext.Provider>
  );
}

export function ImpersonationBanner() {
  const { session, stopImpersonation, isLoading } = useSession();
  const [isStoppingImpersonation, setIsStoppingImpersonation] = useState(false);
  if (!session?.isImpersonating) return null;
  const handleStopImpersonation = async () => {
    setIsStoppingImpersonation(true);
    try {
      await stopImpersonation();
    } catch (e) {
    } finally {
      setIsStoppingImpersonation(false);
    }
  };
  const formatStartTime = (startedAt: string | null) => {
    if (!startedAt) return 'Unknown';
    const start = new Date(startedAt);
    const now = new Date();
    const diffMs = now.getTime() - start.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h ${diffMinutes % 60}m ago`;
    return start.toLocaleDateString();
  };

  return (
    <div className="bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800 px-4 py-2 sticky top-0 z-50" data-testid="impersonation-banner">
      <div className="flex items-center justify-between max-w-7xl mx-auto">
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2 text-red-800 dark:text-red-200">
            <AlertTriangle className="w-5 h-5" />
            <Badge variant="destructive" className="px-2 py-1">READ ONLY MODE</Badge>
          </div>
          <div className="text-sm text-red-700 dark:text-red-300">
            <span className="font-medium">Viewing as:</span>
            <span className="ml-1 font-semibold" data-testid="impersonated-user-name">{session.user?.first_name} {session.user?.last_name} ({session.user?.username})</span>
            <Badge variant="outline" className="ml-2 text-xs">{session.user?.role}</Badge>
          </div>
          <div className="flex items-center text-xs text-red-600 dark:text-red-400">
            Started: {formatStartTime(session.startedAt)}
          </div>
        </div>
        <Button variant="destructive" size="sm" onClick={handleStopImpersonation} disabled={isStoppingImpersonation || isLoading} data-testid="stop-impersonation-button">
          {isStoppingImpersonation ? 'Stopping...' : 'Stop Impersonation'}
        </Button>
      </div>
    </div>
  );
}
